<template>
    <div class="print-container row">
        <div class="form print-controls col-12 col-md-2">

            <div class="form-group">
                <label for="sizeFactor">Marking size:</label>
                <select :disabled="controlsDisabled" v-model="sizeFactor" @change="updateSize">
                    <option value="1">Small</option>
                    <option value="3">Medium</option>
                    <option value="6">Large</option>
                </select>
            </div>

            <div class="form-group">
                <label for="selectedPageSize">Select page size: </label>
                <select :disabled="controlsDisabled" id="paper-size" v-model="selectedPageSize">
                    <option v-for="(dimensions, key) in pageSizes" :key="key" :value="dimensions">
                        {{ key }}
                    </option>
                </select>
            </div>

            <div class="form-group">
                <label for="pageOrientation">Page orientation:</label>
                <select :disabled="controlsDisabled" v-model="paperOrientation">
                    <option value="p">Portrait</option>
                    <option value="l">Landscape</option>
                </select>
            </div>
            <div class="form-group">
                <label>
                    <input :disabled="controlsDisabled" type="checkbox" @change="toggleMarkingsHighlight"/>
                    Highlight Markings
                </label>
                <label>
                    <input :disabled="controlsDisabled" :checked="true" type="checkbox" @change="toggleCheckMarkingsCollision"/>
                    Show Markings Collisions
                </label>
            </div>

            <div class="form-group">
                <button :disabled="controlsDisabled" class="btn btn-primary" @mouseup="print">Print layout</button>
                <div class="instructions">
                    <span>Instructions:</span>
                    <ol>
                        <li>Set a marking size that suits your needs.</li>
                        <li>Set paper size/orientation.</li>
                        <li>Move markings by holding the icon and drag the marking to wanted location (moving the markings only affects the print view).</li>
                        <li>Print the layout.</li>
                    </ol>
                </div>
            </div>
        </div>
        <div ref="printPreview" class="col-12 col-md-10 print-preview">
            <!-- Overlay container -->
            <div class="overlay-container" :class="{ 'loading': isLoading }">
                <div class="loading-message" v-if="isLoading">
                    {{ loadingText }}
                </div>
                <!-- Zoom widget container -->
                <div v-if="!isLoading" class="zoom-widget">
                    <button class="zoom-button pt-0" @click="zoomInOut('out')">&ndash;</button>
                    <div class="zoom-text">{{ pageZoom }}</div>
                    <button class="zoom-button" @click="zoomInOut('in')">+</button>
                </div>
                <!-- Main container -->
                <div class="paper d-flex justify-content-center"
                     ref="docContainer"
                     :style="{ width: paperWidth + 'px', height: paperHeight + 'px' }">
                    <div
                        class="markings-container"
                        ref="markingsContainer"
                        :style="{ width: page.width + 'px', height: page.height + 'px' }">
                        <img
                            ref="docImg"
                            :src="page.url"
                            alt="Layout Image"
                            @load="onLoad">
                        <template
                            v-for="(marking, markingIndex) in markings"
                            v-if="marking.page === page.page">
                            <Marking
                                :pageHeight="page.height"
                                :pageWidth="page.width"
                                :marking-index="markingIndex"
                                v-if="!marking.project_marking_tool_id"
                                :marking="marking"
                                @markingMouseDown="handleMarkingClick"
                                marking-size="small">
                            </Marking>
                            <ProjectMarking
                                v-else
                                :pageWidth="page.width"
                                :pageHeight="page.height"
                                :marking-index="markingIndex"
                                :marking="marking"
                                :project-tools="projectTools"
                                @markingMouseDown="handleMarkingClick"
                                marking-size="small">
                            </ProjectMarking>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PanZoom from 'panzoom';
import interact from 'interactjs';
import Marking from "@mobile/components/global/Marking";
import ProjectMarking from "@mobile/components/global/ProjectMarking";
import jsPDF from "jspdf";
import { toPng } from 'html-to-image';
import { toJpeg } from 'html-to-image';

export default {
    components: {
        Marking, ProjectMarking
    },
    props: {
        file: {
            required: true,
            type: Object,
            default: {}
        },
        markings: {
            type: Array,
            required: false,
            default: []
        },
        page: {
            type: Object,
            required: true
        },
        projectTools: {
            type: Array,
            required: false,
            default: []
        },
        openPage: {
            type: Number,
            required: false,
            default: 1
        },
        moveMarkingIndex: {
            type: Number,
            required: false
        },
    },
    data() {
        return {
            renderQuality: 2,
            needsUpdate: false,
            loadingText: 'Loading, please wait...',
            isPrinting: false,
            controlsDisabled: false,
            sizeFactor: 6,
            markingIndex: 0,
            originalSizes: [],
            paperWidth: 100,
            paperHeight: 100,
            paperOrientation: 'p',
            pageZoom: "Auto",
            isCollisionTesting: true,
            isLoading: true,
            isDraggingMarking: false,
            transform: {
                x: 0,
                y: 0,
                scale: 1
            },
            selectedPageSize: [595.28, 841.89],
            pageSizes: {
                "A0": [2383.94, 3370.39],
                "A1": [1683.78, 2383.94],
                "A2": [1190.55, 1683.78],
                "A3": [841.89, 1190.55],
                "A4": [595.28, 841.89],
                "A5": [419.53, 595.28],
                "A6": [297.64, 419.53],
                "A7": [209.76, 297.64],
                "A8": [147.40, 209.76],
                "A9": [104.88, 147.40],
                "A10": [73.70, 104.88],
                "B0": [2834.65, 4008.19],
                "B1": [2004.09, 2834.65],
                "B2": [1417.32, 2004.09],
                "B3": [1000.63, 1417.32],
                "B4": [708.66, 1000.63],
                "B5": [498.90, 708.66],
                "B6": [354.33, 498.90],
                "B7": [249.45, 354.33],
                "B8": [175.75, 249.45],
                "B9": [124.72, 175.75],
                "B10": [87.87, 124.72],
                "C0": [2599.37, 3676.54],
                "C1": [1836.85, 2599.37],
                "C2": [1298.27, 1836.85],
                "C3": [918.43, 1298.27],
                "C4": [649.13, 918.43],
                "C5": [459.21, 649.13],
                "C6": [323.15, 459.21],
                "C7": [229.61, 323.15],
                "C8": [161.57, 229.61],
                "C9": [113.39, 161.57],
                "C10": [79.37, 113.39],
                "LEGAL": [612.00, 1008.00],
                "LETTER": [612.00, 792.00],
                "TABLOID": [792.00, 1224.00]
            },
        };
    },
    mounted() {
        this.paperWidth = this.page.width;
        this.paperHeight = this.page.height;
        this.storeOriginalSizes();
        this.updateSize();
        if (this.isCollisionTesting) {
            this.handleCollisions();
        }
    },

    beforeDestroy() {
        if (this.panInstance) {
            this.panInstance.dispose();
        }

        if (this.interactInstance) {
            this.interactInstance.unset();
        }
    },

    watch: {
        selectedPageSize(newSize) {
            this.onPageSizeChange(newSize);
        },
        paperOrientation(newOrientation) {
            this.onPageSizeChange(this.selectedPageSize);
        }
    },

    updated() {
        if (this.needsUpdate) {
            this.updateSize();
            this.needsUpdate = false;
        }
    },
    methods: {

        checkCollisions() {
            const paperContainer = document.querySelector('.paper');
            const markingWraps = paperContainer.querySelectorAll('.marking-wrap');
            markingWraps.forEach((wrap, index) => {
                const rect1 = wrap.getBoundingClientRect();
                const marker = wrap.querySelector('.collision-marker');

                let collisionDetected = false;

                markingWraps.forEach((otherWrap, otherIndex) => {
                    if (index === otherIndex) return;

                    const rect2 = otherWrap.getBoundingClientRect();

                    if (this.isCollision(rect1, rect2)) {
                        collisionDetected = true;

                        if (!marker) {
                            const hasCollisionMarker = wrap.querySelector('.collision-marker') !== null;
                            if (!hasCollisionMarker) {
                                const newMarker = this.createCollisionMarker();
                                wrap.appendChild(newMarker);
                            }
                        }
                    }
                });

                if (!collisionDetected && marker) {
                    marker.remove();
                }
            });
        },

        isCollision(rect1, rect2) {
            return !(rect1.right < rect2.left ||
                rect1.left > rect2.right ||
                rect1.bottom < rect2.top ||
                rect1.top > rect2.bottom);
        },

        createCollisionMarker() {
            const marker = document.createElement('div');
            marker.classList.add('collision-marker');
            return marker;
        },

        handleMarkingClick(marking, markingIndex, event) {
            this.markingIndex = markingIndex;
        },

        toggleMarkingsHighlight() {
            const paperContainer = document.querySelector('.paper');
            const isHighlighted = paperContainer.classList.toggle('highlighted');

            const svgElements = paperContainer.querySelectorAll('.marking-wrap line, .marking-wrap rect, .marking-wrap circle, .marking-wrap path, .marking-wrap text');

            svgElements.forEach(element => {
                if (isHighlighted) {
                    element.setAttribute(element.tagName === 'text' ? 'fill' : 'stroke', 'red');
                } else {
                    element.removeAttribute(element.tagName === 'text' ? 'fill' : 'stroke');
                }
            });
        },

        toggleCheckMarkingsCollision() {
            this.isCollisionTesting = !this.isCollisionTesting;

            if (this.isCollisionTesting) {
                this.checkCollisions();
            } else {
                this.removeCollisions();
            }
        },

        removeCollisions() {
            const paperContainer = document.querySelector('.paper');
            const markers = paperContainer.querySelectorAll('.collision-marker');
            markers.forEach(marker => marker.remove());
        },

        handleCollisions() {
            if (this.isCollisionTesting) {
                this.checkCollisions();
            }
        },

        zoomInOut(direction) {
            if (this.panInstance) {
                let zoomFactor = 0.7;
                if (direction === 'in') {
                    zoomFactor = 1.3;
                }
                const rect = this.$refs.printPreview.getBoundingClientRect();
                this.panInstance.smoothZoom(rect.width / 2, rect.height / 2, zoomFactor);
            }
        },

        onPageSizeChange(newSize) {

            let pageRatio = newSize[0] / newSize[1];
            if (this.paperOrientation === 'l') {
                pageRatio = newSize[1] / newSize[0];
            }

            const imageWidth = this.page.width;
            const imageHeight = this.page.height;

            if (pageRatio === 1) {
                this.paperWidth = imageWidth;
                this.paperHeight = imageHeight;
                return;
            }

            const imageRatio = imageWidth / imageHeight;

            if (imageRatio > pageRatio) {
                this.paperWidth = imageWidth;
                this.paperHeight = imageWidth / pageRatio;
            } else {
                this.paperHeight = imageHeight;
                this.paperWidth = imageHeight * pageRatio;
            }
        },

        storeOriginalSizes() {
            const markingWraps = this.$refs.docContainer.querySelectorAll('.marking-wrap');

            this.originalSizes = [];

            markingWraps.forEach(marking => {
                const originalWidth = parseFloat(window.getComputedStyle(marking).width);
                const originalHeight = parseFloat(window.getComputedStyle(marking).height);
                const originalFontSize = parseFloat(window.getComputedStyle(marking).fontSize);

                this.originalSizes.push({
                    element: marking,
                    width: originalWidth,
                    height: originalHeight,
                    fontSize: originalFontSize,
                });
            });
        },

        updateSize() {
            this.originalSizes.forEach(sizeData => {
                const {element, width, height, fontSize} = sizeData;

                const newWidth = width * this.sizeFactor;
                const newHeight = height * this.sizeFactor;
                const newFontSize = fontSize * this.sizeFactor;

                element.style.width = `${newWidth}px`;
                element.style.height = `${newHeight}px`;
                element.style.fontSize = `${newFontSize}px`;
            });

            if (this.isCollisionTesting) {
                this.handleCollisions();
            }
        },

        getZoom(zoomTransform) {
            this.pageZoom = Math.round(zoomTransform.scale * 100) + '%' || 'Auto';
        },

        print() {
            this.controlsDisabled = true;

            if (this.isCollisionTesting) {
                this.removeCollisions();
            }

            if (this.panInstance) {
                this.panInstance.pause();

                this.loadingText = 'Generating PDF...';
                this.isLoading = true;

                this.$nextTick(() => {
                    setTimeout(() => {
                        this.generatePDF().then(() => {

                        }).finally(() => {
                            this.isLoading = false;
                            this.controlsDisabled = false;
                            this.panInstance.resume();
                            setTimeout(() => {
                                if (this.isCollisionTesting) {
                                    this.checkCollisions();
                                }
                            }, 50);
                        });
                    }, 50);
                });
            }
        },

        async generatePDF() {
            let fileName = this.file.name ? this.file.name : "layout";
            let pageNumber = this.page.page ? this.page.page : 1;

            const doc = this.$refs.markingsContainer;

            try {
                // const dataUrl = await toPng(doc, {
                //     width: this.page.width * this.renderQuality,
                //     height: this.page.height * this.renderQuality,
                //     style: {
                //         transform: `scale(${this.renderQuality})`,
                //         transformOrigin: 'top left',
                //     },
                // });
                const dataUrl = await toJpeg(doc, {
                    quality: 0.95,
                    width: this.page.width * this.renderQuality,
                    height: this.page.height * this.renderQuality,
                    style: {
                        transform: `scale(${this.renderQuality})`,
                        transformOrigin: 'top left',
                    },
                });

                const pdf = new jsPDF(this.paperOrientation, 'mm', this.selectedPageSize);

                const img = new Image();
                img.src = dataUrl;

                await new Promise((resolve) => {
                    img.onload = () => {
                        const pageWidth = pdf.internal.pageSize.getWidth();
                        const pageHeight = pdf.internal.pageSize.getHeight();

                        const canvasWidth = img.width;
                        const canvasHeight = img.height;

                        const scaleFactor = Math.min(pageWidth / canvasWidth, pageHeight / canvasHeight);
                        const imgWidth = canvasWidth * scaleFactor;
                        const imgHeight = canvasHeight * scaleFactor;

                        pdf.addImage(dataUrl, 'PNG', 0, 0, imgWidth, imgHeight);
                        pdf.compress = true;

                        pdf.save(fileName + '_' + pageNumber + '.pdf');

                        resolve();
                    };
                });
            } catch (error) {
                console.error('Error generating image or PDF:', error);
            }
        },

        onLoad() {
            this.onPageSizeChange(this.selectedPageSize);

            this.isLoading = false;
            this.isLoading = false;

            if (!this.panInstance) {
                let that = this;

                this.panInstance = PanZoom(this.$refs.docContainer, {
                    zoomDoubleClickSpeed: 1,
                    autocenter: true,
                    maxZoom: 20,
                    minZoom: 0.05,
                    onDoubleClick: () => false,
                });

                this.$nextTick(() => {
                    that.scale = this.panInstance.getTransform().scale;
                });

                this.panInstance.on('zoom', (e) => {
                    const transform = e.getTransform();
                    that.scale = transform.scale;
                    that.getZoom(transform);
                });

                this.panInstance.on('pan', (e) => {
                    const transform = e.getTransform();
                    that.transform.x = transform.x;
                    that.transform.y = transform.y;

                });
                this.initializeInteract(this.panInstance);
            }
        },

        initializeInteract(panInstance) {
            let that = this;
            if (panInstance) {
                this.interactInstance = interact('.marking-wrap').draggable({
                    listeners: {
                        start(event) {
                            panInstance.pause();
                            that.isDraggingMarking = true;

                            if (!event.target.__position) {
                                const transform = window.getComputedStyle(event.target).transform;
                                const matrix = transform.match(/^matrix\((.+)\)$/);
                                let position = {x: 0, y: 0};

                                if (matrix) {
                                    const values = matrix[1].split(', ');
                                    position.x = parseFloat(values[4]) || 0;
                                    position.y = parseFloat(values[5]) || 0;
                                }
                                event.target.__position = position;
                            }
                        },
                        move(event) {

                            let zoomScale = that.scale || 1;

                            let position = event.target.__position;
                            position.x += event.dx / zoomScale;
                            position.y += event.dy / zoomScale;

                            event.target.__position = position;

                            event.target.style.transform = `translate(${position.x}px, ${position.y}px)`;
                        },
                        end: (event) => {
                                if (((this.markings[this.markingIndex].type !== 'custom') || this.markings[this.markingIndex].icon === 'area') &&
                                    (this.markings[this.markingIndex].type !== 'firestop') &&
                                    (this.markings[this.markingIndex].type !== 'camera') &&
                                    (this.markings[this.markingIndex].type !== 'note') &&
                                    (this.markings[this.markingIndex].type !== 'gallery')
                                ) {
                                    panInstance.resume();
                                    return;
                                }

                            let zoomScale = that.scale || 1;

                            const container = this.$refs.markingsContainer;
                            const targetRect = event.target.getBoundingClientRect();
                            const ancestorRect = container.getBoundingClientRect();

                            const offset = this.markingOffset(ancestorRect, event, this.markingIndex, zoomScale);

                            // Both Negative
                            let targetX = targetRect.left;
                            let targetY = targetRect.top;

                            // Negative Y, Positive X
                            if (offset === "+-") {
                                targetX = targetRect.right;
                                targetY = targetRect.top;
                            }

                            // Negative X, Positive Y
                            if (offset === "-+") {
                                targetX = targetRect.left;
                                targetY = targetRect.bottom;
                            }

                            // Positive X, Positive Y
                            if (offset === "++") {
                                targetX = targetRect.right;
                                targetY = targetRect.bottom;
                            }

                            const relativeX = (targetX - ancestorRect.left) / zoomScale;
                            const relativeY = (targetY - ancestorRect.top) / zoomScale;

                            event.target.__position = {x: 0, y: 0};
                            event.target.style.transform = '';

                            this.changeMarkingPosition(this.markingIndex, relativeX, relativeY);
                            panInstance.resume();
                            that.isDraggingMarking = false;
                        }
                    }
                });
            }
        },

        markingOffset(parentRect, event, index, zoomScale) {
            let markingX = this.markings[index].x;
            let mouseX = ((((event.clientX - parentRect.left) / zoomScale) / this.page.width) * 100);

            let markingY = this.markings[index].y;
            let mouseY = ((((event.clientY - parentRect.top) / zoomScale) / this.page.height) * 100);

            let offset = "--";

            if (mouseX > markingX && mouseY < markingY) {
                offset = "+-";
            }
            if (mouseX > markingX && mouseY > markingY) {
                offset = "++";
            }
            if (mouseX < markingX && mouseY > markingY) {
                offset = "-+";
            }
            return offset;
        },

        changeMarkingPosition(index, newX, newY) {
            this.$set(this.markings, index, {
                ...this.markings[index],
                x1: (newX / this.page.width) * 100,
                y1: (newY / this.page.height) * 100
            });
            this.needsUpdate = true;
            this.$forceUpdate();
        },
    }
};
</script>

<style scoped>
::v-deep .collision-marker {
    position: relative;
    width: 10px;
    height: 10px;
    margin-left: -5px;
    margin-top: -5px;
    background: rgba(226, 6, 19, 0.6);
    left: 50%;
    top: 50%;
    border-radius: 50%;
    outline: 1px solid rgba(226, 6, 19, 0.6);
    animation: ripple 2.5s infinite;
}

.instructions li{
    padding-left: 4px;
    font-size: 12px;
}

.instructions ol{
    padding-left: 20px;
}

.instructions {
    padding-top: 12px;
}

@keyframes ripple {
    0% {
        outline-width: 10px;
        outline-color: rgba(226, 6, 19, 0.6);
        background: rgba(226, 6, 19, 0.6);
        transform: scale(1);
    }
    50% {
        outline-color: rgba(226, 6, 19, 0.3);
        background: rgba(226, 6, 19, 0.3);
    }
    100% {
        outline-width: 50px;
        outline-color: rgba(226, 6, 19, 0);
        background: rgba(226, 6, 19, 0);
        transform: scale(2);
    }
}

.zoom-widget {
    display: inline-flex;
    background-color: #ffffff;
    border: solid 1px #a1a1a1;
    border-radius: 50px;
    width: fit-content;
    padding: 3px 5px;
    position: absolute;
    left: 50%;
    transform: translate(-50%, 0);
    z-index: 100;
}

.zoom-text {
    text-align: center;
    width: 6ch;
    padding-top: 2px;
    font-size: 1rem;
}

.zoom-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: none;
    color: white;
    font-size: 24px;
    font-weight: bold;
    cursor: pointer;
    background-color: #6c757d;
    border-color: #6c757d;
}

.zoom-button:hover {
    background-color: #dc2626;
    border-color: #dc2626;
}

.zoom-button:focus {
    outline: none;
}

.print-container {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;

    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
}

.print-controls {
    height: 100%;
}

.print-preview {
    width: 100%;
    height: 100%;
    background-color: #858585;
    padding: 20px;
    overflow: hidden;
    border: 1px solid #858585;
}

.overlay-container {
    position: relative;
    width: 100%;
    height: 100%;
}

.overlay-container.loading::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: -20px;
    background: #858585;
    z-index: 10;
}

.loading-message {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 1.5rem;
    color: #ffffff;
    text-align: center;
    z-index: 20;
    animation: pulse 1.5s infinite;
}

.markings-container {
    position: absolute;
}

.paper {
    background-color: #fff;
    box-shadow: 30px 30px 0px 0px rgba(0, 0, 0, 0.3);
}

@keyframes pulse {
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
    100% {
        opacity: 1;
    }
}
</style>
